'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Service } from '../../types/services';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { Button } from '../ui/button';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { CarouselWall } from '../ui/carousel-wall';
import { Paragraph } from '../ui/paragraph';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

export function Services({
  services,
  subtitle,
  title,
}: {
  services: Service[];
  subtitle: string;
  title: string;
}) {
  const locale = useLocaleStore(s => s.currentLocale);

  const localeId = locale === 'en' ? 'our-services' : 'nase-sluzby';

  const options = {
    align: 'start' as const,
  };

  return (
    <section
      id={localeId}
      className={cn(
        'ea-section-big__center',
        'flex flex-col items-center justify-center gap-8 sm:gap-12 lg:gap-16',
        'bg-[#FAFAFA]',
      )}
    >
      <div className={cn('custom-section')}>
        <div className={cn('custom-container', 'flex items-end justify-between')}>
          <Heading subtitle={subtitle} title={title} />
          <CarouselControls
            className={{
              main: 'hidden sm:flex',
              button:
                'hover:bg-secondary-80 h-12 w-12 rounded-full border-none bg-secondary text-white shadow-none hover:text-white',
              next: 'text-white hover:text-white',
              prev: 'text-white hover:text-white',
            }}
          />
        </div>
      </div>
      <div className={cn('custom-section', 'pr-0')}>
        <div className={cn('custom-container', 'relative')}>
          <CarouselWall className="bg-[#FAFAFA]" />
          <Carousel
            options={options}
            className={{
              container: 'w-full gap-4 overflow-visible',
              wrapper: 'w-full overflow-visible',
              main: 'w-full overflow-visible',
              slide: 'h-full w-full max-w-[340px] sm:max-w-[480px] lg:max-w-[540px]',
            }}
            slides={services.map((service, index) => (
              <ServiceCard service={service} key={service.slug} locale={locale} index={index} />
            ))}
          />
        </div>
      </div>
    </section>
  );
}

function ServiceCard({
  service,
  index,
  locale,
}: {
  service: Service;
  index?: number;
  locale: string;
}) {
  const { t } = useTranslations();

  const href =
    index === 0
      ? '/bozp'
      : index === 1
        ? `${locale === 'en' ? '/revision' : locale === 'sk' ? '/revizie' : '/revize'}`
        : service.slug;

  return (
    <>
      <div className="hidden h-full w-full items-stretch justify-between gap-8 rounded-[24px] border-2 border-[#EDEDED] bg-white p-6 sm:flex">
        <div className="flex w-full flex-col items-start justify-between">
          <div className="flex h-[72px] w-[72px] items-center justify-center rounded-[24px] bg-primary">
            <Image
              src={service?.image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(service?.image, service?.name)}
              width={40}
              height={40}
              className="object-contain"
              sizes="40px"
            />
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <div className="flex w-full flex-col items-start justify-start gap-2">
              <Heading tag="h4" title={service.name} />
              <Paragraph
                size="lg"
                innerHtml={service.short_description}
                color="muted-foreground"
                className="w-full"
              />
            </div>
            <Button asChild variant={'gray'}>
              <Link prefetch={false} href={href}>
                {t('text792')}
              </Link>
            </Button>
          </div>
        </div>
        <Image
          src={service?.images?.[0]?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(service?.images?.[0], service?.name)}
          width={130}
          height={353}
          className="h-[353px] w-[130px] min-w-[130px] rounded-[24px] object-cover object-center"
          sizes="130px"
        />
      </div>
      <div className="flex h-full w-full flex-col items-start justify-start gap-8 rounded-[24px] border border-border bg-white p-6 sm:hidden">
        <div className="flex w-full items-start justify-between">
          <div className="flex h-[72px] w-[72px] items-center justify-center rounded-[24px] bg-primary">
            <Image
              src={service?.image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(service?.image, service?.name)}
              width={40}
              height={40}
              className="object-contain"
              sizes="40px"
            />
          </div>
          <Image
            src={service?.images?.[0]?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(service?.images?.[0], service?.name)}
            width={120}
            height={120}
            sizes="120px"
            className="h-[120px] w-[120px] rounded-[24px] object-cover object-center"
          />
        </div>
        <div className="flex h-full w-full flex-col items-start justify-between gap-4">
          <div className="flex w-full flex-col items-start justify-start gap-2">
            <Heading tag="h3" title={service.name} />
            <Paragraph size="lg" innerHtml={service.short_description} color="muted-foreground" />
          </div>
          <Button asChild variant={'gray'}>
            <Link prefetch={false} href={service.slug}>
              {t('text792')}
            </Link>
          </Button>
        </div>
      </div>
    </>
  );
}
